import { Home, Tag, Banknote } from 'lucide-react';
import { ReunionTransaction } from '../../../hooks/useReunionMensuelleData';
import {
  PeriodBucket, getPeriodKeyForDate, buildPeriodCategoryCounts, buildPeriodColumnAverages, getPriceBracket, PRICE_BRACKET_LABELS,
} from '../../../utils/reunionMensuelle';
import { Scope, getColumnForTransaction } from './ScopeSelector';
import StackedBarCard from './StackedBarCard';
import TrendChartCard from './TrendChartCard';

interface VentesTabProps {
  transactions: ReunionTransaction[];
  periods: PeriodBucket[];
  scope: Scope;
}

const PROPERTY_TYPES = ['Appartement', 'Maison', 'Immeuble', 'Box', 'Terrain', 'Parking'];

export default function VentesTab({ transactions, periods, scope }: VentesTabProps) {
  const inScope = transactions.filter(tx => getColumnForTransaction(tx, scope) !== null);
  const getPeriodKey = (tx: ReunionTransaction) => getPeriodKeyForDate(tx.compromise_date, periods);

  const byTypologie = buildPeriodCategoryCounts(
    inScope, periods, PROPERTY_TYPES, getPeriodKey, tx => tx.property_type
  );

  const byTranche = buildPeriodCategoryCounts(
    inScope, periods, PRICE_BRACKET_LABELS, getPeriodKey, tx => getPriceBracket(tx.sale_price)
  );

  const avgPriceByTypologie = buildPeriodColumnAverages(
    inScope, periods, PROPERTY_TYPES, getPeriodKey, tx => tx.property_type, tx => tx.sale_price
  );
  const typologieColumns = PROPERTY_TYPES.map(t => ({ key: t, label: t }));
  const formatEuros = (v: number) => Math.round(v).toLocaleString('fr-FR');

  return (
    <div className="space-y-5">
      <StackedBarCard
        icon={<Home size={16} className="text-indigo-600" />}
        iconBg="bg-indigo-50"
        title="Ventes par typologie"
        periods={periods}
        categories={PROPERTY_TYPES}
        data={byTypologie}
        getTransactions={(periodKey, categoryKey) =>
          inScope.filter(tx => getPeriodKey(tx) === periodKey && tx.property_type === categoryKey)
        }
        infoText="Chaque vente est rangée dans le mois/trimestre de sa date de compromis. On compte le nombre de ventes par typologie de bien sur le mois (dans le périmètre société/agence sélectionné)."
      />
      <StackedBarCard
        icon={<Tag size={16} className="text-amber-600" />}
        iconBg="bg-amber-50"
        title="Ventes par tranche de prix (net vendeur)"
        periods={periods}
        categories={PRICE_BRACKET_LABELS}
        data={byTranche}
        getTransactions={(periodKey, categoryKey) =>
          inScope.filter(tx => getPeriodKey(tx) === periodKey && getPriceBracket(tx.sale_price) === categoryKey)
        }
        infoText="Chaque vente est rangée dans le mois/trimestre de sa date de compromis. On compte le nombre de ventes par tranche de prix net vendeur sur le mois (dans le périmètre société/agence sélectionné)."
      />
      <TrendChartCard
        icon={<Banknote size={16} className="text-emerald-600" />}
        iconBg="bg-emerald-50"
        title="Prix net vendeur moyen par typologie"
        periods={periods}
        columns={typologieColumns}
        data={avgPriceByTypologie}
        valueFormatter={formatEuros}
        valueSuffix=" €"
        getTransactions={(periodKey, columnKey) =>
          inScope.filter(tx => getPeriodKey(tx) === periodKey && tx.property_type === columnKey && tx.sale_price !== null)
        }
        infoText="Chaque vente est rangée dans le mois/trimestre de sa date de compromis. On fait la moyenne du prix net vendeur des ventes du mois pour chaque typologie de bien."
      />
    </div>
  );
}
